import { UI_MAPPING_CONFIG } from './config';
import { Attribute, AttributeTranslations, Option, OptionsTranslations, Product, ProductTranslations } from './product.types';
import { StationeryProduct } from '@shared/utils/print-price/types';
import { CardDelivery } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { SimpleTextAttribute } from './components/SimpleTextAttribute';
import { DieCutIconAttribute } from './components/DieCutIconAttribute';
import { ColumnTextAttribute } from './components/ColumnTextAttribute';
import { DigitalDeliveryAttribute } from './components/premiumDigital/DigitalDeliveryAttribute';
import { AttributeConfig } from './config.types';
import { FoilChoiceAttribute } from './components/FoilChoiceAttribute';
import { PaperTypeAttribute } from './components/PaperTypeAttribute';
import { useTranslation } from 'react-i18next';
import { buildAttributeTransPath, buildOptionTransPath, buildProductTypeTransPath } from './utils/buildTranslationPaths';

type AttributeProductComponents = Attribute['components'];

const componentIDMapping: Record<string, AttributeProductComponents[keyof AttributeProductComponents]['component']> = {
  ['SimpleTextAttribute']: SimpleTextAttribute,
  ['DieCutIconAttribute']: DieCutIconAttribute,
  ['ColumnTextAttribute']: ColumnTextAttribute,
  ['DigitalDeliveryAttribute']: DigitalDeliveryAttribute,
  ['FoilChoiceGroup']: FoilChoiceAttribute,
  ['PaperTypeAttribute']: PaperTypeAttribute,
  ['none']: null,
};

const mapComponentDefinition = (definition: AttributeConfig['componentDefinitions']): Attribute['components'] => {
  const keys = Object.keys(definition);

  // Map all componentDefinitions to components
  const components: Attribute['components'] = keys.reduce((acc, key) => {
    const entry = definition[key as keyof typeof definition];

    const component = componentIDMapping[entry.componentId];

    if (component === undefined) {
      throw new Error('Improperly defined attribute component mapping');
    }

    return {
      ...acc,
      [key]: {
        component,
        props: entry.props,
      },
    };
  }, {} as Attribute['components']);

  return components;
};

export const useProductToUIMapping = () => {
  const { t } = useTranslation('stationeryPriceConfigMapping');

  const getProductWithUIMapping = (productInput: StationeryProduct): (StationeryProduct & Product) | null => {
    const productUIMapping = getProductTypeInfo(productInput.productType);

    if (!productUIMapping || !productInput) {
      return null;
    }

    return {
      ...productInput,
      ...productUIMapping,
    };
  };

  const getProductTypeInfo = (productType: string): Product | null => {
    const productMapping = UI_MAPPING_CONFIG.productTypes.find(prod => prod.type === productType);

    if (!productMapping) {
      return null;
    }

    const translations = t(buildProductTypeTransPath(productType), { returnObjects: true }) as ProductTranslations;

    if (!translations || typeof translations !== 'object') {
      return null;
    }

    return {
      ...productMapping,
      translations,
    };
  };

  /**
   * Get the UI Configuration Data for a given Product Attribute
   * @returns Attribute config data
   */
  const getAttributeById = (attributeId: string): Attribute | null => {
    const attribute = UI_MAPPING_CONFIG.attributes.find(attr => attr.attributeId === attributeId);

    if (!attribute) {
      return null;
    }

    const translations = t(buildAttributeTransPath(attributeId), { returnObjects: true }) as AttributeTranslations;

    if (!translations || typeof translations !== 'object') {
      return null;
    }

    // Options sorted by sortOrder. If some options do not have sort order, those appear
    // at the end in array order.
    const sortedOptions = attribute.options.sort((a, b) => {
      return (a.sortOrder ?? 999) - (b.sortOrder ?? 999);
    });
    attribute.options = sortedOptions;

    const components = mapComponentDefinition(attribute.componentDefinitions);

    return {
      ...attribute,
      components,
      translations,
    };
  };

  const getOptionById = (attributeId: string, optionId: string): Option | null => {
    const attribute = UI_MAPPING_CONFIG.attributes.find(attr => attr.attributeId === attributeId);

    if (!attribute) {
      return null;
    }

    const option = attribute?.options.find(opt => opt.optionId === optionId);

    if (!option) {
      return null;
    }

    const translations = t(buildOptionTransPath(attributeId, optionId), { returnObjects: true }) as OptionsTranslations;

    if (!translations || typeof translations !== 'object') {
      return null;
    }

    return {
      ...option,
      translations: {
        selector: {
          displayName: translations.selector.displayName,
        },
        lineItem: {
          displayName: translations.lineItem.displayName,
        },
      },
    };
  };

  const getDeliveryMethodByCardDelivery = (cardDelivery: CardDelivery) => {
    const deliveryMethods = UI_MAPPING_CONFIG.deliveryMethods;
    const targetMethod = deliveryMethods.find(m => m.deliveryMethod === cardDelivery);

    if (!targetMethod) {
      throw new Error('Tried getting invalid delivery method');
    }

    return targetMethod;
  };

  return {
    getProductWithUIMapping,
    getDeliveryMethodByCardDelivery,
    getProductTypeInfo,
    getAttributeById,
    getOptionById,
  };
};
